import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import toast from 'react-hot-toast';
import './PostBattleScreen.css';
import { getRarityColor, getRarityClass, getRarityGlow } from '../utils/rarityUtils';
import ItemTooltipContent from './shared/ItemTooltipContent';
import LevelUpCardPick from './shared/LevelUpCardPick';
import TooltipBadge from './shared/TooltipBadge';
import { formatCurrency } from './shared/formatCompact';
import { effectiveMusicVolume } from '../utils/settings';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// XP formula matching server: floor((sqrt(1 + 8 * floor(xp/1000)) - 1) / 2) + 1
function characterLevel(xp) {
  return Math.floor((Math.sqrt(1 + 8 * Math.floor(xp / 1000)) - 1) / 2) + 1;
}

function xpForLevel(level) {
  return ((level - 1) * level / 2) * 1000;
}

// All class abilities with their level requirements
const CLASS_ABILITIES = {
  Fighter: [
    { name: 'Boost', level: 1, desc: 'All hits become critical for 2 minutes' },
    { name: 'Defensive Stance', level: 3, desc: 'Strike + gain DEF bonus' },
    { name: 'Precise Stab', level: 7, desc: 'Auto-crit with 2 damage' },
    { name: 'Recoil Strike', level: 12, desc: '3 damage, take half back' },
    { name: 'Critical Blow', level: 40, desc: 'Auto-crit with 4 damage' },
    { name: 'Death Strike', level: 80, desc: '8 damage, 10% instant kill' },
  ],
  Thief: [
    { name: 'Flawless Dodge', level: 1, desc: 'Dodge all attacks for 2 minutes' },
    { name: 'Steal', level: 3, desc: 'Attempt to steal an item' },
    { name: 'Sneak Attack', level: 5, desc: 'Bonus sneak damage' },
    { name: 'Hide', level: 13, desc: 'Hidden for 10 minutes' },
    { name: 'Critical Sneak Attack', level: 40, desc: 'Massive sneak damage' },
    { name: 'Assassin Strike', level: 70, desc: '6 damage, 15% instant kill' },
    { name: 'Superior Assassin Strike', level: 100, desc: '6 damage, 40% instant kill' },
  ],
  Mage: [
    { name: 'Mana Fountain', level: 1, desc: 'Spells cost 0 MP for 2 minutes' },
    { name: 'Power Cast', level: 10, desc: 'Next spell deals double damage (3🔵 MP)' },
  ],
  Cleric: [
    { name: 'Miracle', level: 1, desc: 'Full heal + remove all debuffs' },
    { name: 'Spontaneous Casting', level: 7, desc: 'Next spell is free' },
  ],
  Psion: [
    { name: 'Third Eye', level: 1, desc: 'Double all damage for 2 minutes' },
    { name: 'Mind Over Matter', level: 9, desc: 'Convert HP to MP' },
  ],
  Druid: [
    { name: 'Charm', level: 1, desc: 'Attempt to charm enemy as pet' },
    { name: 'Friendship', level: 1, desc: 'Force charm if enemy is close in level' },
    { name: 'Dismiss', level: 1, desc: 'Release your current pet' },
    { name: 'Entangle', level: 12, desc: 'Debuff enemy ATT/DEF/miss' },
    { name: 'Greater Entangle', level: 50, desc: 'Stronger entangle' },
    { name: 'Perfect Entangle', level: 150, desc: 'Ultimate entangle' },
  ],
};

const STAT_NAMES = {
  str: { label: 'Strength', abbr: 'STR', icon: '⚔️', desc: 'Bonus melee damage on all physical attacks', detail: 'Adds damage to every melee hit, ability strike, and physical skill. Also boosts Smithing crafting.' },
  agl: { label: 'Agility', abbr: 'AGL', icon: '🏃', desc: 'Stealing, hiding, fleeing, evasion (Thief)', detail: 'Improves Steal/Mug success, Hide checks, and Run escape chance. Thieves gain AGLmod% Evasion and AGLmod×2 Sneak Attack bonus.' },
  vit: { label: 'Vitality', abbr: 'VIT', icon: '❤️', desc: 'Boosts HP gained per level (hit die + VIT mod)', detail: 'VIT modifier (floor((VIT - 10) / 2)) is added to your hit die roll each level. Higher VIT = more HP per level. Also used for Mind Over Matter (HP→MP).' },
  men: { label: 'Mentality', abbr: 'MEN', icon: '🔮', desc: 'MP per level · spell power · MP regen', detail: 'MEN modifier added to MP gained per level. Scales ALL spell damage, heal power, and spell resistance penetration. Regen max(1, MENmod) MP per turn in combat. Also boosts Alchemy crafting.' },
  cha: { label: 'Charisma', abbr: 'CHA', icon: '✨', desc: 'Pet charm & shop discounts', detail: 'Improves Charm success rate vs monsters. Gives shop buy/sell discounts (modifier × 1.5%). Primary stat for Druids and pet builds.' },
};

// v640.3: Hit-dice tables — mirrors server/game/ClassAbilities.js for stat preview parity
const CLASS_HIT_DICE = { Fighter: 10, Thief: 8, Mage: 4, Cleric: 6, Psion: 4, Druid: 8 };
const AC_HIT_DICE = {
  'Warrior': 12, 'Assassin': 10, 'Ranger': 10, 'Paladin': 10, 'Spellsword': 8, 'Soulknife': 8,
  'Rogue': 8, 'Animal Lord': 8, 'Vigilante': 8, 'Divine Agent': 8, 'Shaman': 8,
  'Illusionist': 6, 'Conjurer': 6, 'Priest': 6, 'Fatespinner': 6, 'Telepath': 6,
  'Mystic Theurge': 6, 'Psychic Theurge': 6, 'Cerebremancer': 4, 'Wizard': 4, 'Metamind': 4,
};
function getClientHitDie(baseClass, advancedClass) {
  if (advancedClass && AC_HIT_DICE[advancedClass]) return AC_HIT_DICE[advancedClass];
  return CLASS_HIT_DICE[baseClass] || 6;
}

const PostBattleScreen = ({ battleResult, character, questSummary, onClose, onCharacterUpdate, onOpenGuide, onOpenSpellbook }) => {
  const [phase, setPhase] = useState('rewards'); // 'rewards', 'fanfare', 'levelup', 'cardpick', 'tierunlock'
  const [statAllocation, setStatAllocation] = useState({ str: 0, agl: 0, vit: 0, men: 0, cha: 0 });
  const [fanfareStep, setFanfareStep] = useState(0); // 0 = banner, 1+ = reveal chips/abilities one-by-one
  const chimeRef = useRef(null);
  // Card pick state (Phase 3 integration)
  const [cardChoices, setCardChoices] = useState(null); // null = not loaded, [] = none available
  const [forgeTokens, setForgeTokens] = useState(0);
  const [selectedCardKey, setSelectedCardKey] = useState(null);
  const [cardChoicesError, setCardChoicesError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResurrecting, setIsResurrecting] = useState(false);
  const [error, setError] = useState(null);
  const [animatedXp, setAnimatedXp] = useState(0);
  const [animatedGold, setAnimatedGold] = useState(0);
  const [showItems, setShowItems] = useState(false);
  const [tooltipItem, setTooltipItem] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showDeckStats, setShowDeckStats] = useState(false);
  const [tierUnlocked, setTierUnlocked] = useState(null);
  const audioRef = useRef(null);
  const tooltipRef = useRef(null);

  // Adjust tooltip if it overflows viewport
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el || !tooltipItem) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom > window.innerHeight - 8) {
      el.style.top = `${Math.max(8, window.innerHeight - rect.height - 8)}px`;
    }
  });

  const handleLootMouseEnter = (e, item) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const tooltipWidth = 260;
    const x = Math.min(rect.right + 8, window.innerWidth - tooltipWidth - 12);
    setTooltipItem(item);
    setTooltipPos({ x: Math.max(8, x), y: rect.top });
  };
  const handleLootMouseLeave = () => setTooltipItem(null);

  const { status, rewards, music } = battleResult;
  const isVictory = status === 'victory';
  const isDefeat = status === 'defeat';
  const isFled = status === 'fled';

  // Announce newly-unlocked spell tiers once, the moment the results appear.
  const unlockedSpells = rewards?.unlockedSpells || [];
  useEffect(() => {
    if (isVictory && unlockedSpells.length > 0) {
      const top = unlockedSpells[0];
      const extra = unlockedSpells.length - 1;
      toast.success(`✨ New spell unlocked: ${top.name}${extra > 0 ? ` (+${extra} more)` : ''}! Equip it in your Spellbook.`, { duration: 6000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate level-up info
  const newXp = character?.experience || 0;
  const oldXp = newXp - (rewards?.xp || 0);
  const newLevel = characterLevel(newXp);
  // Use the character's confirmed DB level as the baseline for stat allocation.
  // If a prior level-up was skipped (glitch/disconnect), character.level is
  // lower than the XP-derived level. The server validates stat points against
  // (DB level → XP level), so the client MUST use DB level to match.
  const oldLevel = character?.level || 1;
  const didLevelUp = isVictory && newLevel > oldLevel;
  const levelsGained = newLevel - oldLevel;
  const xpToNextLevel = xpForLevel(newLevel + 1);
  const xpCurrentLevel = xpForLevel(newLevel);
  const xpProgress = ((newXp - xpCurrentLevel) / (xpToNextLevel - xpCurrentLevel)) * 100;

  // Find new abilities unlocked
  const classAbilities = CLASS_ABILITIES[character?.class] || [];
  const newAbilities = classAbilities.filter(
    a => a.level > oldLevel && a.level <= newLevel
  );

  // Every 4 levels, player gets an ability point  check if any level in the range qualifies
  const abilityPointLevels = [];
  for (let l = oldLevel + 1; l <= newLevel; l++) {
    if (l % 4 === 0) abilityPointLevels.push(l);
  }
  const totalAbilityPoints = abilityPointLevels.length;

  // Skill points gained (matches server formula in routes/player.js)
  const _skillPtsAt = (lvl) => (lvl < 5 ? 0 : Math.floor((lvl - 5) / 3) + 1);
  const skillPointsGained = didLevelUp ? (_skillPtsAt(newLevel) - _skillPtsAt(oldLevel)) : 0;

  // Deck rewards crossed in this level range — mirrors levelRewardsForRange()
  // in server/game/DeckGenerator.js (v800 cadence).
  const deckRewards = (() => {
    const out = { slots: 0, forge: 0, trash: 0 };
    for (let l = oldLevel + 1; l <= newLevel; l++) {
      if (l % 5 === 0) { out.slots += 1; out.trash += 1; }
      else if (l % 3 === 0) { out.forge += 1; }
    }
    return out;
  })();
  const deckSlotsGained = deckRewards.slots;

  // Round-number / Crown / round-number milestones in the range
  const milestones = (() => {
    const out = [];
    for (let l = oldLevel + 1; l <= newLevel; l++) {
      if (l === 25) out.push({ level: l, kind: 'crown', label: '👑 Champion of Loria', sub: 'You have proven yourself worthy.' });
      else if (l === 50) out.push({ level: l, kind: 'major', label: '⚜️ Hero of the Realm', sub: 'Half a century of battle behind you.' });
      else if (l === 75) out.push({ level: l, kind: 'major', label: '🔥 Mythic Hero', sub: 'Few in Loria walk this far.' });
      else if (l === 100) out.push({ level: l, kind: 'legendary', label: '🌟 Legendary Ascension', sub: 'Your name will be remembered.' });
      else if (l === 10) out.push({ level: l, kind: 'minor', label: '🌳 Skill Tree Mastery', sub: 'Heritage node now available.' });
    }
    return out;
  })();
  const hasMajorMilestone = milestones.some(m => m.kind === 'crown' || m.kind === 'major' || m.kind === 'legendary');

  // Class-themed flavor for the fanfare banner
  const CLASS_FLAVOR = {
    Fighter: 'Your blade hums with new resolve.',
    Thief: 'The shadows bend a little closer to you.',
    Mage: 'Arcane currents thread through your veins.',
    Cleric: 'A warm light kindles in your chest.',
    Psion: 'Your mind expands beyond its walls.',
    Druid: 'The wilds whisper your name in approval.',
  };
  const classFlavor = CLASS_FLAVOR[character?.class] || 'You feel a surge of power.';

  // Build the ordered reveal list for the fanfare interstitial
  const fanfareReveals = (() => {
    const out = [];
    out.push({ id: 'stat', kind: 'chip', icon: '⚡', label: `+${levelsGained} Stat Point${levelsGained > 1 ? 's' : ''}` });
    if (skillPointsGained > 0) out.push({ id: 'skill', kind: 'chip', icon: '🌳', label: `+${skillPointsGained} Skill Point${skillPointsGained > 1 ? 's' : ''}` });
    if (totalAbilityPoints > 0) out.push({ id: 'ability', kind: 'chip', icon: '✨', label: `+${totalAbilityPoints} Ability Point${totalAbilityPoints > 1 ? 's' : ''}` });
    if (deckSlotsGained > 0) out.push({ id: 'deck', kind: 'chip', icon: '🃏', label: `+${deckSlotsGained} Deck Slot${deckSlotsGained > 1 ? 's' : ''}` });
    if (deckRewards.trash > 0) out.push({ id: 'trash', kind: 'chip', icon: '🗑', label: `+${deckRewards.trash} Trash Token${deckRewards.trash > 1 ? 's' : ''}` });
    if (deckRewards.forge > 0) out.push({ id: 'forge', kind: 'chip', icon: '⚒', label: `+${deckRewards.forge} Forge Token${deckRewards.forge > 1 ? 's' : ''}` });
    newAbilities.forEach((a, i) => out.push({ id: `ab-${i}`, kind: 'ability', ability: a }));
    milestones.forEach((m) => out.push({ id: `m-${m.level}`, kind: 'milestone', milestone: m }));
    return out;
  })();

  const totalAllocated = Object.values(statAllocation).reduce((s, v) => s + v, 0);
  const pointsRemaining = levelsGained - totalAllocated;

  // Play music
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !music) return;

    let musicFile = music;
    // v737.1: play level-up music from the moment the screen mounts when a
    // level-up is pending — not just from the `fanfare` phase onward. The
    // earlier behavior kept battle music looping during the XP count-up.
    if (didLevelUp) {
      musicFile = 'levelup.mp3';
    }

    audio.src = `/music/${musicFile}`;
    audio.volume = effectiveMusicVolume();
    audio.loop = true;
    audio.play().then(() => {
      // Mark that user audio is working  enables auto-play everywhere
      const { setUserHasInteracted } = require('../utils/audioState');
      setUserHasInteracted(true);
    }).catch(() => {});

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, [music, phase, didLevelUp]);

  // Animate XP and gold counting up
  useEffect(() => {
    if (!isVictory || !rewards) return;

    const xpTarget = rewards.xp || 0;
    const goldTarget = rewards.gold || 0;
    const duration = 1200;
    const startTime = Date.now();

    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic

      setAnimatedXp(Math.floor(xpTarget * eased));
      setAnimatedGold(Math.floor(goldTarget * eased));

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        setTimeout(() => setShowItems(true), 300);
      }
    };

    const delay = setTimeout(() => requestAnimationFrame(tick), 500);
    return () => clearTimeout(delay);
  }, [isVictory, rewards]);

  // Fanfare phase: play chime on entry, stagger reveal items, listen for skip key, auto-advance
  useEffect(() => {
    if (phase !== 'fanfare') return;
    setFanfareStep(0);
    // Play SFX chime layered over music (file is optional — failure is silent)
    try {
      const chime = chimeRef.current;
      if (chime) {
        chime.src = '/sounds/levelup_chime.mp3';
        chime.volume = Math.min(1, effectiveMusicVolume() * 1.1);
        chime.currentTime = 0;
        chime.play().catch(() => {});
      }
    } catch (e) { /* ignore */ }

    const stepDelay = 280; // ms between reveal items
    const initialDelay = 700; // ms before first reveal (lets banner land)
    const totalSteps = fanfareReveals.length;
    const timers = [];
    for (let i = 0; i < totalSteps; i++) {
      timers.push(setTimeout(() => setFanfareStep(s => Math.max(s, i + 1)), initialDelay + i * stepDelay));
    }

    // Keyboard skip / advance
    const onKey = (e) => {
      if (e.key === ' ' || e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        // First press: reveal all items instantly; second press: advance phase
        setFanfareStep(s => {
          if (s < totalSteps) return totalSteps;
          setPhase('levelup');
          return s;
        });
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('keydown', onKey);
    };
  }, [phase, fanfareReveals.length]);

  // Phase 3: Pre-fetch card choices as soon as we know there's a level-up
  // (fires while still on rewards/fanfare so they're ready by the time we reach stat allocation)
  useEffect(() => {
    if (!didLevelUp) return;
    if (cardChoices !== null) return; // already fetched (success or empty array)
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/player/levelup/card-choices`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!response.ok) {
          if (!cancelled) setCardChoices([]); // no choices available
          return;
        }
        const data = await response.json();
        if (cancelled) return;
        setCardChoices(Array.isArray(data.choices) ? data.choices : []);
        if (typeof data.forgeTokens === 'number') setForgeTokens(data.forgeTokens);
      } catch (err) {
        if (!cancelled) {
          setCardChoices([]);
          setCardChoicesError('Could not load card choices');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [didLevelUp, cardChoices]);

  const handleStatIncrease = (stat) => {
    if (pointsRemaining <= 0) return;
    setStatAllocation(prev => ({ ...prev, [stat]: prev[stat] + 1 }));
  };

  const handleStatDecrease = (stat) => {
    if (statAllocation[stat] <= 0) return;
    setStatAllocation(prev => ({ ...prev, [stat]: prev[stat] - 1 }));
  };

  const handleConfirmLevelUp = async () => {
    if (pointsRemaining !== 0) return;
    // If card choices are available, transition to cardpick phase first
    if (Array.isArray(cardChoices) && cardChoices.length > 0) {
      setError(null);
      setPhase('cardpick');
      return;
    }
    // Otherwise, submit stats now (no card pick)
    await submitLevelUp(null);
  };

  const handleConfirmCardPick = async (cardKey) => {
    await submitLevelUp(cardKey);
  };

  const submitLevelUp = async (cardPick) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const body = { statIncrease: statAllocation };
      if (cardPick) body.cardPick = cardPick;
      const response = await fetch(`${API_BASE_URL}/api/player/levelup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (response.ok) {
        await onCharacterUpdate();
        if (data.tierUnlocked) {
          setTierUnlocked(data.tierUnlocked);
          setPhase('tierunlock');
        } else {
          onClose();
        }
      } else {
        setError(data.error || 'Failed to level up');
        setIsSubmitting(false);
      }
    } catch (err) {
      setError('Network error  try again');
      setIsSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (didLevelUp && phase === 'rewards') {
      setPhase('fanfare');
    } else if (phase === 'fanfare') {
      setPhase('levelup');
    } else {
      onClose();
    }
  };

  const handleResurrect = () => {
    setIsResurrecting(true);
    // Refresh character data from server (now at full HP in respawn area)
    onCharacterUpdate();
    // Brief delay to let the game state refresh arrive, then close
    setTimeout(() => {
      onClose();
    }, 1200);
  };

  // REWARDS PHASE 
  if (phase === 'rewards') {
    return (
      <div className="post-battle-overlay">
        <audio ref={audioRef} loop />
        <div className={`post-battle-panel ${status}`}>

          {/* Victory */}
          {isVictory && (
            <>
              <div className="pb-header victory">
                <div className="pb-icon"></div>
                <h2>Victory!</h2>
                {battleResult.eliteTier && (
                  <div className={`pb-elite-badge pb-tier-${battleResult.eliteTier}`}>
                    {battleResult.eliteTier === 'legendary' ? '👑 LEGENDARY' : battleResult.eliteTier === 'champion' ? '💀 CHAMPION' : '⚠️ ELITE'}
                  </div>
                )}
              </div>

              {/* Elite Tier Flavor Text */}
              {battleResult.eliteTier && (
                <div className={`pb-tier-flavor tier-flavor-${battleResult.eliteTier}`}>
                  {battleResult.eliteTier === 'legendary' ? 'You have vanquished a legendary foe! Songs will be sung of this triumph!' :
                   battleResult.eliteTier === 'champion' ? 'A champion-tier enemy has fallen before you!' :
                   'You defeated an elite variant — well fought!'}
                </div>
              )}

              {/* First Kill Discovery */}
              {battleResult.firstKillBonus && (
                <div className="pb-first-kill fade-in">
                  <span className="first-kill-icon">📖</span>
                  <span className="first-kill-text">
                    New Bestiary Entry! <strong>{battleResult.firstKillBonus.monster}</strong> — +{battleResult.firstKillBonus.xp} bonus XP, +{battleResult.firstKillBonus.gold} bonus gold
                  </span>
                </div>
              )}

              {/* Punching-Up Bonus */}
              {battleResult.punchingUpBonus && (
                <div className="pb-first-kill fade-in" style={{ borderColor: '#f59e0b' }}>
                  <span className="first-kill-icon">⚔️</span>
                  <span className="first-kill-text" style={{ color: '#f59e0b' }}>
                    Punching Up! +{battleResult.punchingUpBonus}% bonus XP & gold
                  </span>
                  {onOpenGuide && (
                    <button
                      type="button"
                      onClick={() => onOpenGuide('challenge-loops')}
                      style={{
                        marginLeft: '8px',
                        background: 'rgba(245, 158, 11, 0.12)',
                        border: '1px solid rgba(245, 158, 11, 0.35)',
                        color: '#f59e0b',
                        borderRadius: '999px',
                        padding: '3px 8px',
                        cursor: 'pointer',
                        fontSize: '0.72rem'
                      }}
                    >
                      Guide
                    </button>
                  )}
                </div>
              )}

              <div className="pb-rewards">
                <div className="pb-reward-row xp-row">
                  <span className="reward-icon"></span>
                  <span className="reward-label">Experience</span>
                  <span className="reward-value xp-value">+{animatedXp} XP</span>
                  {rewards?.restedXpBonus > 0 && (
                    <span className="reward-rested-tag">💤 +{rewards.restedXpBonus} rested<TooltipBadge id="xp.rested" /></span>
                  )}
                </div>

                <div className="pb-reward-row gold-row">
                  <span className="reward-icon"></span>
                  <span className="reward-label">Gold</span>
                  <span className="reward-value gold-value">+{animatedGold} gp</span>
                </div>

                {/* Seed XP */}
                {rewards?.seedXpGained && Object.keys(rewards.seedXpGained).length > 0 && (
                  <div className="pb-seed-xp">
                    <div className="seed-xp-header">Seed XP</div>
                    {Object.entries(rewards.seedXpGained).map(([seed, xp]) => {
                      const isDiscovered = rewards.discoveredSeeds && rewards.discoveredSeeds.includes(seed);
                      return (
                        <div key={seed} className={`pb-reward-row seed-row${isDiscovered ? ' seed-discovered' : ''}`}>
                          <span className="reward-icon">{isDiscovered ? '🌱' : '🌿'}</span>
                          <span className="reward-label">{seed}{isDiscovered ? ' (NEW!)' : ''}</span>
                          <span className="reward-value seed-value">+{xp} XP</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Seed Discovery Banner */}
                {rewards?.discoveredSeeds && rewards.discoveredSeeds.length > 0 && (
                  <div className="pb-seed-discovery fade-in">
                    <div className="seed-discovery-header">🌱 Seeds Discovered!</div>
                    <div className="seed-discovery-text">
                      Your mastery is spreading! You've begun to sense {rewards.discoveredSeeds.length === 1 ? 'a new magical seed' : 'new magical seeds'}:
                    </div>
                    <div className="seed-discovery-list">
                      {rewards.discoveredSeeds.map(seed => (
                        <span key={seed} className="seed-discovery-badge">{seed}</span>
                      ))}
                    </div>
                    <div className="seed-discovery-hint">Visit your Character Sheet to learn spells in {rewards.discoveredSeeds.length === 1 ? 'this seed' : 'these seeds'}!</div>
                  </div>
                )}

                {/* Newly unlocked spell tiers */}
                {rewards?.unlockedSpells && rewards.unlockedSpells.length > 0 && (
                  <div className="pb-seed-discovery fade-in" style={{ borderColor: '#f0d98c' }}>
                    <div className="seed-discovery-header" style={{ color: '#f0d98c' }}>✨ New Spells Unlocked!</div>
                    <div className="seed-discovery-text">
                      Your growing mastery unlocks {rewards.unlockedSpells.length === 1 ? 'a stronger spell' : 'stronger spells'}:
                    </div>
                    <div className="seed-discovery-list">
                      {rewards.unlockedSpells.slice(0, 6).map(s => (
                        <span key={s.name} className="seed-discovery-badge">{s.name}</span>
                      ))}
                    </div>
                    <div className="seed-discovery-hint" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                      Equip them in your Spellbook to cast them.
                      {onOpenSpellbook && (
                        <button
                          type="button"
                          onClick={() => { onClose && onClose(); onOpenSpellbook(); }}
                          style={{ padding: '0.2rem 0.6rem', borderRadius: '5px', border: '1px solid #f0d98c', background: 'rgba(240,217,140,0.15)', color: '#f0d98c', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold' }}
                        >
                          📖 Open Spellbook
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Telepath Mind Link: Learned Ability */}
                {rewards?.learnedAbility && (
                  <div className="pb-seed-discovery fade-in" style={{ borderColor: '#a569bd' }}>
                    <div className="seed-discovery-header" style={{ color: '#a569bd' }}>🧠 Mind Link: Ability Learned!</div>
                    <div className="seed-discovery-text">
                      Your telepathic connection with {rewards.learnedAbility.source} allowed you to learn:
                    </div>
                    <div className="seed-discovery-list">
                      <span className="seed-discovery-badge" style={{ background: 'rgba(165,105,189,0.3)', borderColor: '#a569bd' }}>
                        {rewards.learnedAbility.name}
                      </span>
                    </div>
                  </div>
                )}

                {/* Items */}
                {showItems && rewards?.items && rewards.items.length > 0 && (
                  <div className="pb-items fade-in">
                    <div className="items-header">🎁 Loot Found</div>
                    {rewards.items.map((item, i) => (
                      <div key={i} className={`pb-item-row ${getRarityClass(item)}`}
                        onMouseEnter={(e) => handleLootMouseEnter(e, item)}
                        onMouseLeave={handleLootMouseLeave}
                      >
                        <span className="item-icon">
                          {item.type === 'weapon' ? '' :
                           item.type === 'armor' ? '' :
                           item.type === 'restore' ? '' :
                           item.type === 'scroll' ? '' : ''}
                        </span>
                        <span className="item-name" style={{ color: getRarityColor(item), textShadow: getRarityGlow(item) }}>{item.name}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* XP Progress Bar */}
                <div className="pb-xp-bar-section">
                  <div className="xp-bar-label">
                    Level {newLevel}  {formatCurrency(newXp)} / {formatCurrency(xpToNextLevel)} XP
                  </div>
                  <div className="xp-bar-track">
                    <div className="xp-bar-fill" style={{ width: `${Math.min(xpProgress, 100)}%` }} />
                  </div>
                </div>

                {didLevelUp && (
                  <div className="pb-levelup-notice fade-in">
                    🌟 Level Up! You are now <strong>Level {newLevel}</strong>!
                  </div>
                )}
                {didLevelUp && oldLevel < 5 && newLevel >= 5 && (
                  <div className="pb-levelup-notice fade-in" style={{ marginTop: 6, background: 'rgba(131,157,191,0.15)', borderColor: 'rgba(131,157,191,0.55)' }}>
                    🌳 <strong>Skill Tree unlocked!</strong> You earn 1 skill point every 3 levels. Open the Skills panel to spend it — including your race's Heritage node at level 10.
                  </div>
                )}
              </div>

              {/* Bounty Progress */}
              {battleResult.bountyProgress && battleResult.bountyProgress.length > 0 && (
                <div className="pb-bounty-progress fade-in">
                  <div className="bounty-progress-header">📜 Bounty Progress</div>
                  {battleResult.bountyProgress.map((bp) => (
                    <div key={bp.id} className={`pb-bounty-row ${bp.complete ? 'bounty-complete' : ''}`}>
                      <div className="bounty-row-top">
                        <span className="bounty-monster-name">{bp.monster}</span>
                        <span className="bounty-type-badge">{bp.type}</span>
                        <span className={`bounty-count ${bp.complete ? 'complete' : ''}`}>
                          {bp.complete ? '✓ Complete!' : `${bp.current} / ${bp.target}`}
                        </span>
                      </div>
                      <div className="bounty-bar-track">
                        <div
                          className={`bounty-bar-fill ${bp.complete ? 'complete' : ''}`}
                          style={{ width: `${Math.min((bp.current / bp.target) * 100, 100)}%` }}
                        />
                      </div>
                      {bp.complete && (
                        <div className="bounty-complete-hint">
                          Return to a Tavern to claim your reward!
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Achievement Unlocks */}
              {battleResult.newAchievements && battleResult.newAchievements.length > 0 && (
                <div className="pb-achievements fade-in">
                  <div className="achievement-header">🏆 Achievement Unlocked!</div>
                  {battleResult.newAchievements.map((ach, i) => (
                    <div key={i} className="pb-achievement-row">
                      <span className="achievement-icon">{ach.icon}</span>
                      <div className="achievement-info">
                        <span className="achievement-name">{ach.name}</span>
                        <span className="achievement-desc">{ach.description}</span>
                      </div>
                      {ach.reward && (
                        <span className="achievement-reward">+{ach.reward.amount} {ach.reward.type === 'xp' ? 'XP' : ach.reward.type}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Combat Streak */}
              {battleResult.combatStreak >= 3 && (
                <div className="pb-streak fade-in">
                  🔥 Win Streak: <strong>{battleResult.combatStreak}</strong>
                  {battleResult.combatStreak >= 25 ? ' — Legendary!' : battleResult.combatStreak >= 10 ? ' — Unstoppable!' : battleResult.combatStreak >= 5 ? ' — On Fire!' : ''}
                </div>
              )}

              {/* Deck Stats Summary */}
              {battleResult.deckStats && battleResult.deckStats.totalPlayed > 0 && (
                <div className="pb-deck-stats fade-in">
                  <button className="deck-stats-toggle" onClick={() => setShowDeckStats(p => !p)}>
                    🃏 {showDeckStats ? 'Hide' : 'Show'} Card Stats
                  </button>
                  {showDeckStats && (
                    <div className="deck-stats-body">
                      <div className="deck-stats-grid">
                        <div className="deck-stat-cell">
                          <span className="deck-stat-val">{battleResult.deckStats.totalPlayed}</span>
                          <span className="deck-stat-lbl">Cards Played</span>
                        </div>
                        <div className="deck-stat-cell">
                          <span className="deck-stat-val">{battleResult.deckStats.turnsPlayed}</span>
                          <span className="deck-stat-lbl">Turns</span>
                        </div>
                        <div className="deck-stat-cell">
                          <span className="deck-stat-val">{battleResult.deckStats.totalDamage}</span>
                          <span className="deck-stat-lbl">Damage</span>
                        </div>
                        <div className="deck-stat-cell">
                          <span className="deck-stat-val">{battleResult.deckStats.totalBlock}</span>
                          <span className="deck-stat-lbl">Block</span>
                        </div>
                        {battleResult.deckStats.totalHealing > 0 && (
                          <div className="deck-stat-cell">
                            <span className="deck-stat-val">{battleResult.deckStats.totalHealing}</span>
                            <span className="deck-stat-lbl">Healing</span>
                          </div>
                        )}
                        {battleResult.deckStats.cardsExhausted > 0 && (
                          <div className="deck-stat-cell">
                            <span className="deck-stat-val">{battleResult.deckStats.cardsExhausted}</span>
                            <span className="deck-stat-lbl">Exhausted</span>
                          </div>
                        )}
                      </div>

                      {battleResult.deckStats.topCards && battleResult.deckStats.topCards.length > 0 && (
                        <div className="deck-stats-top">
                          <div className="deck-top-header">Most Played</div>
                          {battleResult.deckStats.topCards.map((tc, i) => (
                            <div key={i} className="deck-top-row">
                              <span className="deck-top-name">{tc.name}</span>
                              <span className="deck-top-count">×{tc.count}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {battleResult.deckStats.byType && Object.keys(battleResult.deckStats.byType).length > 0 && (
                        <div className="deck-stats-types">
                          {Object.entries(battleResult.deckStats.byType).map(([type, count]) => (
                            <span key={type} className={`deck-type-badge deck-type-${type}`}>
                              {type}: {count}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* v619: Next-Steps panel — surface the active quest objective + a contextual hint */}
              {(questSummary?.activeObjective || didLevelUp || oldLevel < 3) && (
                <div className="pb-next-steps fade-in">
                  <div className="pb-next-header">🎯 What's next?</div>
                  {questSummary?.activeObjective && (
                    <div className="pb-next-row">
                      <span className="pb-next-icon">📜</span>
                      <span className="pb-next-text">{questSummary.activeObjective}</span>
                    </div>
                  )}
                  {didLevelUp && newLevel >= 5 && (
                    <div className="pb-next-row">
                      <span className="pb-next-icon">🌳</span>
                      <span className="pb-next-text">Open your Skill Tree (<kbd>K</kbd>) to spend skill points.</span>
                    </div>
                  )}
                  {oldLevel < 3 && (
                    <div className="pb-next-row">
                      <span className="pb-next-icon">🃏</span>
                      <span className="pb-next-text">Customize your hand in the Deck panel (🃏 in the top bar).</span>
                    </div>
                  )}
                </div>
              )}

              <button className="pb-continue-btn" onClick={handleContinue}>
                {didLevelUp ? '🌟 Level Up!' : 'Continue Adventuring'}
              </button>
            </>
          )}

          {/* Defeat */}
          {isDefeat && (
            <>
              <div className="pb-header defeat">
                <div className="pb-icon"></div>
                <h2>You Have Fallen</h2>
              </div>

              <div className="pb-death-message">
                <p>Your vision fades to black as you collapse...</p>
                <p className="xp-penalty">You lost some experience (you can't drop below your current level), but your gear and gold are safe. You'll wake up at your homepoint.</p>
                <div className="death-tips">
                  <p className="death-tip-header">💡 Tips for next time:</p>
                  <ul>
                    <li>Stock up on Health Potions from shops</li>
                    <li>Use Block and Heal cards to survive tough fights</li>
                    <li>Try the Run option (flee) against stronger enemies</li>
                    {character?.level >= 4 && <li>Upgrade your gear at a crafting station</li>}
                  </ul>
                </div>
              </div>

              {/* v640.2: Death recap — last 10 combat-log lines so player can see how they fell */}
              {(() => {
                const recap = battleResult?.deathRecap;
                const lines = recap && Array.isArray(recap.lastLog) ? recap.lastLog : [];
                if (lines.length === 0) return null;
                const killer = recap.monsterName
                  ? `${recap.monsterName}${recap.monsterLevel ? ` (Lv ${recap.monsterLevel})` : ''}`
                  : null;
                return (
                  <details className="pb-death-recap" open style={{
                    margin: '0.75rem 0',
                    padding: '0.6rem 0.85rem',
                    background: 'rgba(60,10,10,0.45)',
                    border: '1px solid #7a2222',
                    borderRadius: 6,
                    color: '#f1d4d4',
                    fontSize: '0.92rem',
                  }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#ffb8b8' }}>
                      💀 Last Stand{killer ? ` — slain by ${killer}` : ''}
                      {recap.round ? ` · Round ${recap.round}` : ''}
                    </summary>
                    <ol style={{ margin: '0.5rem 0 0 1.1rem', padding: 0, lineHeight: 1.45 }}>
                      {lines.map((line, idx) => {
                        const isKillingBlow = idx === lines.length - 1;
                        return (
                          <li key={idx} style={{
                            color: isKillingBlow ? '#ff6b6b' : '#e8c8c8',
                            fontWeight: isKillingBlow ? 700 : 400,
                            marginBottom: 2,
                          }}>
                            {line}
                          </li>
                        );
                      })}
                    </ol>
                  </details>
                );
              })()}

              <button className="pb-resurrect-btn" onClick={handleResurrect} disabled={isResurrecting}>
                {isResurrecting ? '✨ Resurrecting...' : '🔮 Rise Again'}
              </button>
            </>
          )}

          {/* Fled */}
          {isFled && (
            <>
              <div className="pb-header fled">
                <div className="pb-icon"></div>
                <h2>Escaped!</h2>
              </div>

              <div className="pb-fled-message">
                <p>You managed to flee from battle.</p>
              </div>

              <button className="pb-continue-btn" onClick={handleContinue}>
                Continue
              </button>
            </>
          )}
        </div>

        {/* Loot Item Tooltip */}
        {tooltipItem && (
          <div
            className="item-tooltip-popup"
            ref={tooltipRef}
            style={{ position: 'fixed', left: tooltipPos.x, top: tooltipPos.y, zIndex: 10001 }}
          >
            <ItemTooltipContent item={tooltipItem} compact />
          </div>
        )}
      </div>
    );
  }

  // FANFARE PHASE — celebratory "LEVEL UP!" interstitial between rewards and stat allocation
  if (phase === 'fanfare') {
    const totalSteps = fanfareReveals.length;
    const allRevealed = fanfareStep >= totalSteps;
    const isLegendary = milestones.some(m => m.kind === 'legendary');
    const isMajor = hasMajorMilestone;
    const panelClass = `post-battle-panel levelup pb-fanfare${isLegendary ? ' pb-fanfare-legendary' : isMajor ? ' pb-fanfare-major' : ''}`;
    return (
      <div className="post-battle-overlay pb-fanfare-overlay" onClick={() => {
        // Click anywhere to reveal-all or advance
        if (!allRevealed) setFanfareStep(totalSteps);
        else setPhase('levelup');
      }}>
        <audio ref={audioRef} loop />
        <audio ref={chimeRef} />
        <div className="pb-fanfare-burst" aria-hidden="true" />
        <div className={panelClass} onClick={(e) => e.stopPropagation()}>
          <div className="pb-fanfare-banner">
            <div className="pb-fanfare-headline">LEVEL UP!</div>
            <div className="pb-fanfare-levels">
              <span className="pb-fanfare-old-level">Lv {oldLevel}</span>
              <span className="pb-fanfare-arrow">→</span>
              <span className="pb-fanfare-new-level">Lv {newLevel}</span>
            </div>
            <div className="pb-fanfare-flavor">{classFlavor}</div>
          </div>

          {fanfareReveals.length > 0 && (
            <div className="pb-fanfare-reveals">
              {fanfareReveals.map((item, idx) => {
                const visible = idx < fanfareStep;
                if (item.kind === 'chip') {
                  return (
                    <div key={item.id} className={`pb-fanfare-chip${visible ? ' pb-fanfare-revealed' : ''}`}>
                      <span className="pb-fanfare-chip-icon">{item.icon}</span>
                      <span className="pb-fanfare-chip-label">{item.label}</span>
                    </div>
                  );
                }
                if (item.kind === 'ability') {
                  return (
                    <div key={item.id} className={`pb-fanfare-ability${visible ? ' pb-fanfare-revealed' : ''}`}>
                      <div className="pb-fanfare-ability-head">
                        <span className="pb-fanfare-ability-tag">✦ Ability Unlocked</span>
                        <span className="pb-fanfare-ability-lvl">Lv {item.ability.level}</span>
                      </div>
                      <div className="pb-fanfare-ability-name">{item.ability.name}</div>
                      <div className="pb-fanfare-ability-desc">{item.ability.desc}</div>
                    </div>
                  );
                }
                if (item.kind === 'milestone') {
                  const m = item.milestone;
                  return (
                    <div key={item.id} className={`pb-fanfare-milestone pb-fanfare-milestone-${m.kind}${visible ? ' pb-fanfare-revealed' : ''}`}>
                      <div className="pb-fanfare-milestone-label">{m.label}</div>
                      <div className="pb-fanfare-milestone-sub">Level {m.level} — {m.sub}</div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}

          <button
            className="pb-continue-btn pb-fanfare-continue"
            onClick={(e) => { e.stopPropagation(); if (!allRevealed) setFanfareStep(totalSteps); else setPhase('levelup'); }}
          >
            {allRevealed ? 'Allocate Stats →' : 'Reveal All'}
          </button>
          <div className="pb-fanfare-hint">Press <kbd>Space</kbd> or click to {allRevealed ? 'continue' : 'reveal all'}</div>
        </div>
      </div>
    );
  }

  // CARD PICK PHASE — deck-building reward selection (between stat allocation and tier unlock)
  if (phase === 'cardpick') {
    return (
      <div className="post-battle-overlay">
        <div className="post-battle-panel levelup" style={{ maxWidth: '760px' }}>
          <LevelUpCardPick
            cardChoices={cardChoices || []}
            forgeTokens={forgeTokens}
            selectedCard={selectedCardKey}
            onSelect={setSelectedCardKey}
            onConfirm={() => handleConfirmCardPick(selectedCardKey)}
            onSkip={() => handleConfirmCardPick(null)}
            onBack={() => { setPhase('levelup'); setSelectedCardKey(null); }}
            isSubmitting={isSubmitting}
            error={error || cardChoicesError}
            level={newLevel}
          />
        </div>
      </div>
    );
  }

  // TIER UNLOCK PHASE — cinematic reveal of newly-available world tier
  if (phase === 'tierunlock' && tierUnlocked) {
    const tierColor = tierUnlocked.color || '#ffd24a';
    return (
      <div className="post-battle-overlay pb-tier-overlay" style={{ '--tier-color': tierColor }}>
        <audio ref={audioRef} loop />
        <div className="pb-tier-burst" aria-hidden="true" />
        <div className="post-battle-panel levelup pb-tier-panel" style={{ textAlign: 'center', borderColor: tierColor }}>
          <div className="pb-tier-eyebrow">⚡ A NEW REALM BECKONS ⚡</div>
          <div className="pb-tier-icon" style={{ color: tierColor }}>{tierUnlocked.icon}</div>
          <h2 className="pb-tier-title" style={{ color: tierColor }}>World Tier Unlocked!</h2>
          <p className="pb-tier-sub">You've reached <strong>Level {newLevel}</strong>. A greater challenge awaits:</p>
          <div className="pb-tier-card" style={{ borderColor: tierColor, color: tierColor, boxShadow: `0 0 30px ${tierColor}66` }}>
            <span className="pb-tier-card-icon">{tierUnlocked.icon}</span>
            <span className="pb-tier-card-name">Tier {tierUnlocked.tier}: {tierUnlocked.name}</span>
          </div>
          <p className="pb-tier-hint">
            Open the World Tier menu (🌍) to advance when you're ready.<br />
            Higher tiers mean tougher monsters — but much better rewards!
          </p>
          <button className="pb-confirm-btn pb-fanfare-continue" onClick={onClose}>
            Continue Adventuring →
          </button>
        </div>
      </div>
    );
  }

  // LEVEL UP PHASE 
  return (
    <div className="post-battle-overlay">
      <audio ref={audioRef} loop />
      <div className="post-battle-panel levelup">
        <div className="pb-header levelup-header">
          <div className="pb-icon"></div>
          <h2>Level Up!</h2>
          <div className="level-badge">Level {oldLevel}  Level {newLevel}</div>
        </div>

        {/* New abilities unlocked */}
        {newAbilities.length > 0 && (
          <div className="pb-new-abilities">
            <h3>✨ New Abilities Unlocked</h3>
            {newAbilities.map((ability, i) => (
              <div key={i} className="new-ability-row">
                <span className="ability-name-badge">{ability.name}</span>
                <span className="ability-desc">{ability.desc}</span>
                <span className="ability-level-badge">Lv.{ability.level}</span>
              </div>
            ))}
          </div>
        )}

        {/* Stat allocation */}
        <div className="pb-stat-allocation">
          <h3>Allocate Stat Points</h3>
          <div className="stat-points-remaining">
            Points remaining: <strong>{pointsRemaining}</strong> / {levelsGained}
          </div>

          {totalAbilityPoints > 0 && (
            <div className="ability-point-notice">
               You earned <strong>{totalAbilityPoints} bonus ability point{totalAbilityPoints > 1 ? 's' : ''}</strong> (every 4 levels)!
            </div>
          )}

          {/* Skill point notification */}
          {(() => {
            const skillPtsOld = oldLevel < 5 ? 0 : Math.floor((oldLevel - 5) / 3) + 1;
            const skillPtsNew = newLevel < 5 ? 0 : Math.floor((newLevel - 5) / 3) + 1;
            const skillPtsGained = skillPtsNew - skillPtsOld;
            if (skillPtsGained > 0) {
              return (
                <div className="ability-point-notice" style={{ borderColor: '#c0a050', background: '#2a2a1a' }}>
                  🌳 You earned <strong>{skillPtsGained} skill point{skillPtsGained > 1 ? 's' : ''}</strong>! Open the Skill Tree (T) to spend them.
                </div>
              );
            }
            return null;
          })()}

          <div className="stat-rows">
            {Object.entries(STAT_NAMES).map(([key, stat]) => {
              const currentVal = character?.[
                key === 'str' ? 'strength' :
                key === 'agl' ? 'agility' :
                key === 'vit' ? 'vitality' :
                key === 'men' ? 'mentality' : 'charisma'
              ] || 0;

              const bonusNote = null; // HP/MP gains are now rolled (hit dice + modifiers) on the server
              const added = statAllocation[key] || 0;
              const rowChanged = added > 0;

              return (
                <div key={key} className={`stat-alloc-wrapper${rowChanged ? ' stat-alloc-changed' : ''}`}>
                  <div className="stat-alloc-row">
                    <span className="stat-icon">{stat.icon}</span>
                    <span className="stat-name">{stat.label}</span>
                    <span className="stat-current">{currentVal}</span>

                    <button
                      className="stat-btn minus"
                      onClick={() => handleStatDecrease(key)}
                      disabled={statAllocation[key] <= 0}
                    >
                      </button>
                    <span className={`stat-added ${statAllocation[key] > 0 ? 'has-points' : ''}`}>
                      +{statAllocation[key]}
                    </span>
                    <button
                      className="stat-btn plus"
                      onClick={() => handleStatIncrease(key)}
                      disabled={pointsRemaining <= 0}
                    >
                      +
                    </button>

                    <span className={`stat-new${rowChanged ? ' stat-new-bumped' : ''}`}>{currentVal + added}</span>
                    {bonusNote && <span className="stat-bonus-note">{bonusNote}</span>}
                  </div>
                  <div className="stat-desc">{stat.desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        {error && <div className="pb-error">{error}</div>}

        {/* v640.3: Build preview — show projected MP gain (deterministic) and HP gain range (hit-die roll) */}
        {(() => {
          const newVit = (character?.vitality || 0) + (statAllocation.vit || 0);
          const newMen = (character?.mentality || 0) + (statAllocation.men || 0);
          const vitMod = Math.floor((newVit - 10) / 2);
          const menMod = Math.floor((newMen - 10) / 2);
          const hitDie = getClientHitDie(character?.class, character?.advanced_class);
          const minHpPerLvl = Math.max(1, 1 + vitMod);
          const maxHpPerLvl = Math.max(1, hitDie + vitMod);
          const avgHpPerLvl = Math.max(1, Math.round((hitDie + 1) / 2 + vitMod));
          const mpPerLvl = Math.max(0, menMod);
          const minHp = minHpPerLvl * levelsGained;
          const maxHp = maxHpPerLvl * levelsGained;
          const avgHp = avgHpPerLvl * levelsGained;
          const totalMp = mpPerLvl * levelsGained;
          const newMaxMp = (character?.max_mp || 0) + totalMp;
          return (
            <div className="pb-build-preview" style={{
              margin: '0.75rem 0',
              padding: '0.7rem 0.9rem',
              background: 'rgba(40,60,90,0.35)',
              border: '1px solid #4a6a90',
              borderRadius: 6,
              color: '#d8e6f5',
              fontSize: '0.92rem',
            }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: '#7fb8ff' }}>
                📈 Build Preview (after Confirm)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 1rem' }}>
                <div>
                  <span style={{ color: '#aab8c8' }}>Max HP gain:</span>{' '}
                  <strong style={{ color: '#ff8888' }}>+{minHp}–{maxHp}</strong>{' '}
                  <span style={{ color: '#88a8c8', fontSize: '0.85em' }}>(avg ~{avgHp}, d{hitDie}{vitMod >= 0 ? '+' : ''}{vitMod}/lvl)</span>
                </div>
                <div>
                  <span style={{ color: '#aab8c8' }}>Max MP:</span>{' '}
                  <strong style={{ color: '#88aaff' }}>{character?.max_mp || 0} → {newMaxMp}</strong>{' '}
                  <span style={{ color: '#88a8c8', fontSize: '0.85em' }}>(+{totalMp}, MEN mod {menMod})</span>
                </div>
                <div>
                  <span style={{ color: '#aab8c8' }}>VIT mod:</span>{' '}
                  <strong>{vitMod >= 0 ? '+' : ''}{vitMod}</strong>
                  {(statAllocation.vit || 0) > 0 && <span style={{ color: '#88c888', fontSize: '0.85em' }}> (was {Math.floor(((character?.vitality || 0) - 10) / 2)})</span>}
                </div>
                <div>
                  <span style={{ color: '#aab8c8' }}>MEN mod:</span>{' '}
                  <strong>{menMod >= 0 ? '+' : ''}{menMod}</strong>
                  {(statAllocation.men || 0) > 0 && <span style={{ color: '#88c888', fontSize: '0.85em' }}> (was {Math.floor(((character?.mentality || 0) - 10) / 2)})</span>}
                </div>
              </div>
              <div style={{ marginTop: 6, fontSize: '0.82em', color: '#7a8a9a', fontStyle: 'italic' }}>
                HP roll is randomized per level; final values may vary within range.
              </div>
            </div>
          );
        })()}

        <button
          className={`pb-confirm-btn${pointsRemaining === 0 && !isSubmitting ? ' pb-confirm-ready' : ''}`}
          onClick={handleConfirmLevelUp}
          disabled={pointsRemaining !== 0 || isSubmitting}
        >
          {isSubmitting ? 'Leveling up...' : `Confirm Level ${newLevel}`}
        </button>

        {/* Confetti burst when all points are allocated (CSS-only, no deps) */}
        {pointsRemaining === 0 && !isSubmitting && (
          <div className="pb-confetti" aria-hidden="true">
            {Array.from({ length: 14 }).map((_, i) => (
              <span key={i} className={`pb-confetti-piece pb-confetti-${i % 7}`} style={{ left: `${(i * 7.3) % 100}%`, animationDelay: `${(i % 5) * 0.08}s` }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PostBattleScreen;
